import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './context/AuthContext';
import AdminLayout from './layouts/AdminLayout';
import { PageLoader } from './components/States';

import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AcceptInvite from './pages/auth/AcceptInvite';

// The console is only reachable after signing in, so its pages are split out of
// the initial bundle.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Providers = lazy(() => import('./pages/providers/Providers'));
const ProviderDetail = lazy(() => import('./pages/providers/ProviderDetail'));
const ProviderReport = lazy(() => import('./pages/providers/ProviderReport'));
const Categories = lazy(() => import('./pages/taxonomy/Categories'));
const Subcategories = lazy(() => import('./pages/taxonomy/Subcategories'));
const Countries = lazy(() => import('./pages/Countries'));
const Roles = lazy(() => import('./pages/Roles'));
const Referrals = lazy(() => import('./pages/Referrals'));
const Sponsors = lazy(() => import('./pages/Sponsors'));
const Plans = lazy(() => import('./pages/Plans'));
const Team = lazy(() => import('./pages/Team'));
const HelpDesk = lazy(() => import('./pages/queries/HelpDesk'));
const PlanUpgrades = lazy(() => import('./pages/queries/PlanUpgrades'));
const Chat = lazy(() => import('./pages/Chat'));
const Finance = lazy(() => import('./pages/Finance'));
const Payouts = lazy(() => import('./pages/Payouts'));
const ProviderInsights = lazy(() => import('./pages/insights/ProviderInsights'));
const BookingInsights = lazy(() => import('./pages/insights/BookingInsights'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Forbidden = lazy(() => import('./pages/Forbidden'));

/** Sends a signed-out visitor to /login, remembering where they were going. */
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader label="Restoring your session" />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

/** Keeps a signed-in user off the auth pages. */
function RedirectIfAuthenticated({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader label="Loading" />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * Route-level permission gate. A user who reaches a URL they cannot use gets a
 * clear explanation rather than a screen of failed requests.
 */
function RequirePermission({ permission, children }) {
  const { can } = useAuth();
  if (!can(permission)) return <Forbidden permission={permission} />;
  return children;
}

/** The first section this user is actually allowed to open. */
function HomeRedirect() {
  const { can } = useAuth();
  if (can('dashboard:read')) return <Navigate to="/dashboard" replace />;
  if (can('provider:read')) return <Navigate to="/providers" replace />;
  if (can('report:read')) return <Navigate to="/finance" replace />;
  return <Navigate to="/profile" replace />;
}

const guard = (permission, element) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
);

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <Login />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <RedirectIfAuthenticated>
            <ForgotPassword />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      {/* Console */}
      <Route
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route
          path="/*"
          element={
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route index element={<HomeRedirect />} />
                <Route path="dashboard" element={guard('dashboard:read', <Dashboard />)} />

                <Route path="providers" element={guard('provider:read', <Providers />)} />
                <Route path="providers/:id" element={guard('provider:read', <ProviderDetail />)} />
                <Route path="providers/:id/report" element={guard('report:read', <ProviderReport />)} />

                <Route path="categories" element={guard('category:read', <Categories />)} />
                <Route path="subcategories" element={guard('subcategory:read', <Subcategories />)} />
                <Route path="countries" element={guard('country:read', <Countries />)} />
                <Route path="roles" element={guard('role:read', <Roles />)} />
                <Route path="plans" element={guard('plan:read', <Plans />)} />
                <Route path="referrals" element={guard('referral:read', <Referrals />)} />
                <Route path="sponsors" element={guard('sponsor:read', <Sponsors />)} />
                <Route path="team" element={guard('team:read', <Team />)} />

                {/* Two queues, no combined landing page of its own -- /queries
                    opens the help desk rather than a 404. */}
                <Route path="queries" element={<Navigate to="/queries/help-desk" replace />} />
                <Route path="queries/help-desk" element={guard('query:read', <HelpDesk />)} />
                <Route
                  path="queries/plan-upgrades"
                  element={guard('query:read', <PlanUpgrades />)}
                />

                {/* Direct chat with gurus and instructors. Its own permission, not query:read:
                    the help desk is a ticketed queue and this is an open conversation. */}
                <Route path="chat" element={guard('chat:read', <Chat />)} />
                <Route path="finance" element={guard('report:read', <Finance />)} />
                <Route path="finance/payouts" element={guard('report:read', <Payouts />)} />
                {/* The group has no landing page of its own, so /insights opens
                    the first board rather than a 404. */}
                <Route path="insights" element={<Navigate to="/insights/providers" replace />} />
                <Route
                  path="insights/providers"
                  element={guard('insight:read', <ProviderInsights />)}
                />
                <Route
                  path="insights/bookings"
                  element={guard('insight:read', <BookingInsights />)}
                />

                <Route path="audit-log" element={guard('audit:read', <AuditLog />)} />
                <Route path="settings" element={guard('setting:read', <Settings />)} />

                <Route path="profile" element={<Profile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
